/*
  Kalisee: free shipping bar amount without decimals.

  The native <free-shipping-bar> in theme.js formats the remaining amount with
  `new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active })`
  and passes no fraction-digit options, so it always renders two decimals ("122,00 RON").
  Nothing in that component reads a theme setting, and theme.js is a minified vendor
  bundle that is never edited.

  So instead of patching the component, we re-render the same message from the element's
  own `threshold` and `total-price` attributes with a decimal-less formatter.

  Two rules keep that safe:

  1. We observe ONLY `threshold` and `total-price`, never the subtree. Those two attributes
     are the whole input of the message, and the native component rewrites the span
     synchronously whenever one of them changes, so our observer callback always runs
     right after it and wins. Observing childList/characterData instead would make our own
     write re-enter the observer and lock the main thread.
  2. The idempotency check compares serialised markup on both sides. `innerHTML` does not
     round-trip: Intl.NumberFormat separates amount and currency with a non-breaking space
     (U+00A0) and the innerHTML getter serialises it back as `&nbsp;`, so a raw string
     comparison is never equal and the write would repeat forever.

  The amount is rounded UP on purpose: if 122,45 RON are still missing, "123 RON" is the
  honest number, while a rounded-down "122 RON" would not reach the threshold.
*/
(() => {
  const SELECTOR = 'free-shipping-bar';
  let formatter = null;
  let probe = null;

  const getFormatter = () => {
    if (formatter) return formatter;

    const locale = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'ro';
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'RON';

    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return formatter;
  };

  // Serialise a candidate string the same way the browser serialises live markup,
  // so the comparison below is a real equality check.
  const serialize = (html) => {
    if (!probe) probe = document.createElement('span');
    probe.innerHTML = html;
    return probe.innerHTML;
  };

  const render = (bar) => {
    const span = bar.querySelector('span');
    const template = bar.getAttribute('unreached-message');

    if (!span || !template) return;

    const threshold = parseFloat(bar.getAttribute('threshold'));
    const totalPrice = parseFloat(bar.getAttribute('total-price'));

    // Threshold reached, or attributes not ready: leave the native message alone.
    if (!isFinite(threshold) || !isFinite(totalPrice) || totalPrice >= threshold) return;

    const remaining = Math.ceil((threshold - totalPrice) / 100);
    const html = template.replace(/\{\{[^}]*\}\}/g, getFormatter().format(remaining));

    if (span.innerHTML !== serialize(html)) span.innerHTML = html;
  };

  const watch = (bar) => {
    if (bar.dataset.freeShippingAmount) return;
    bar.dataset.freeShippingAmount = 'no-decimals';

    render(bar);

    new MutationObserver(() => render(bar)).observe(bar, {
      attributes: true,
      attributeFilter: ['threshold', 'total-price'],
    });
  };

  document.querySelectorAll(SELECTOR).forEach(watch);

  // The cart drawer and the cart page re-render on cart changes, so bars appear later too.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        if (node.matches(SELECTOR)) watch(node);
        node.querySelectorAll(SELECTOR).forEach(watch);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
